/**
 * Where the back-office talks to.
 *
 * Decided by host name, not by build mode.
 *
 * NEXT_PUBLIC_APP_MODE is baked into the bundle when the image is built, and one
 * image is deployed to every region, so a build-time value cannot name a
 * region's API. It was never set anywhere either — not in the Dockerfile, not in
 * the chart — so every deployment fell through to the development branch and the
 * back-office called localhost:3000. That is why the regional consoles could not
 * even sign in.
 *
 * The historical hosts are listed because they do not follow the regional rule:
 * their API lives on pyxiscience.fr while the console lives on .com. Everything
 * else follows one rule, admin.<instance> talks to api.<instance> and links to
 * <instance>.app, so a new region needs no change here.
 */

interface Environment {
  websiteUrl: string;
  apiUrl: string;
  appMode: string;
  scriptsDocUrl: string;
}

const dev: Environment = {
  websiteUrl: `http://localhost:5173`,
  apiUrl: `http://localhost:3000`,
  appMode: 'development',
  scriptsDocUrl: 'https://test.scripts.doc.pyxiscience.com/',
};

/** admin.pyxiscience.com and its two prefixed siblings, as they are today. */
const HISTORICAL: Record<string, Environment> = {
  'test.admin.pyxiscience.com': {
    websiteUrl: `https://test.app.pyxiscience.com`,
    apiUrl: `https://test.api.pyxiscience.fr`,
    appMode: 'test',
    scriptsDocUrl: 'https://test.scripts.doc.pyxiscience.com/',
  },
  'staging.admin.pyxiscience.com': {
    websiteUrl: `https://staging.app.pyxiscience.com`,
    apiUrl: `https://staging.api.pyxiscience.fr`,
    appMode: 'staging',
    scriptsDocUrl: 'https://staging.scripts.doc.pyxiscience.com/',
  },
  'admin.pyxiscience.com': {
    websiteUrl: `https://app.pyxiscience.com`,
    apiUrl: `https://api.pyxiscience.fr`,
    appMode: 'production',
    scriptsDocUrl: 'https://scripts.doc.pyxiscience.com/',
  },
};

/**
 * A regional console: admin.eu, admin.us, and the non-production instances,
 * which carry their environment in the same segment (admin.test-eu).
 */
const REGIONAL = /^admin\.([a-z0-9-]+)\.pyxiscience\.com$/;

/**
 * Instances whose editor does not live on a front end of their own.
 *
 * The content console has no front end at all: its Fargate task runs the
 * backend, this console and the MyST renderer, and the load balancer routes
 * only the first two. The host the rule below derives,
 * content.app.pyxiscience.com, exists in no DNS zone — so the editor iframe
 * pointed at nothing and the authoring console could not author.
 *
 * It borrows the European front end. The editor is a user-interface shell: it
 * talks to its parent by postMessage and calls no API of its own, and what it
 * edits is the global catalogue, which by definition carries no personal data.
 * Nothing crosses a residency boundary. It does create a dependency on eu-prod,
 * and that is the price until the content instance serves its own front — at
 * which point this map loses its only entry.
 */
const BORROWED_WEBSITE: Record<string, string> = {
  content: 'https://eu.app.pyxiscience.com',
};

const scriptsDocFor = (instance: string): string => {
  if (instance.startsWith('test-')) return 'https://test.scripts.doc.pyxiscience.com/';
  if (instance.startsWith('staging-')) return 'https://staging.scripts.doc.pyxiscience.com/';
  return 'https://scripts.doc.pyxiscience.com/';
};

const appModeFor = (instance: string): string => {
  if (instance.startsWith('test-')) return 'test';
  if (instance.startsWith('staging-')) return 'staging';
  return 'production';
};

/**
 * The browser knows where it is. On the server it does not, so the deployment
 * states it: a runtime variable, read when the page is rendered rather than
 * written into the bundle, which is exactly the distinction that broke this.
 */
const currentHost = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return process.env.BACKOFFICE_HOST ?? 'localhost';
};

export function resolveEnvironment(host: string): Environment {
  const historical = HISTORICAL[host];
  if (historical) {
    return historical;
  }

  const regional = REGIONAL.exec(host);
  if (regional) {
    const instance = regional[1] as string;
    return {
      websiteUrl: BORROWED_WEBSITE[instance] ?? `https://${instance}.app.pyxiscience.com`,
      apiUrl: `https://api.${instance}.pyxiscience.com`,
      appMode: appModeFor(instance),
      scriptsDocUrl: scriptsDocFor(instance),
    };
  }

  // Anything else is someone running the back-office on their own machine.
  return dev;
}

const getEnvironment = () => resolveEnvironment(currentHost());

export default getEnvironment;
